/**
 * Cloudflare Turnstile 人机验证 Hook
 * 统一管理 Turnstile 脚本加载、Widget 渲染和 Token 获取
 */

import { useState, useEffect, useRef, useCallback } from "react";

/** Turnstile 全局类型声明 */
declare global {
  interface Window {
    turnstile?: {
      render: (
        element: string | HTMLElement,
        options: TurnstileRenderOptions,
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

/** Turnstile 渲染选项 */
interface TurnstileRenderOptions {
  sitekey: string;
  callback: (token: string) => void;
  "error-callback"?: () => void;
  "expired-callback"?: () => void;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "compact" | "flexible";
}

/** useTurnstile 选项 */
export interface UseTurnstileOptions {
  /** 站点密钥，默认从 meta 标签读取 */
  siteKey?: string;
  /** 主题，默认跟随系统 */
  theme?: "light" | "dark" | "auto";
  /** 尺寸 */
  size?: "normal" | "compact" | "flexible";
  /** 验证成功回调 */
  onSuccess?: (token: string) => void;
  /** 验证失败回调 */
  onError?: () => void;
  /** 验证过期回调 */
  onExpire?: () => void;
}

/** useTurnstile 返回值 */
export interface UseTurnstileReturn {
  /** 容器 ref，绑定到渲染容器 */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** 当前 token */
  token: string;
  /** 加载状态 */
  isLoading: boolean;
  /** 是否可用 */
  isAvailable: boolean;
  /** 重置验证 */
  reset: () => void;
  /** 移除 Widget */
  remove: () => void;
}

/** Turnstile 脚本 URL */
const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js";

/** 脚本加载超时时间 */
const SCRIPT_LOAD_TIMEOUT = 5000;

/**
 * 从 meta 标签获取站点密钥
 */
function getSiteKeyFromMeta(): string {
  if (typeof document === "undefined") return "";
  const meta = document.querySelector<HTMLMetaElement>(
    'meta[name="turnstile-site-key"]',
  );
  return meta?.content || "";
}

/**
 * 获取当前主题
 */
function getCurrentTheme(): "light" | "dark" {
  if (typeof document === "undefined") return "light";
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

/**
 * Cloudflare Turnstile 人机验证 Hook
 */
export function useTurnstile(
  options: UseTurnstileOptions = {},
): UseTurnstileReturn {
  const {
    siteKey: customSiteKey,
    theme = "auto",
    size = "normal",
    onSuccess,
    onError,
    onExpire,
  } = options;

  // 状态
  const [token, setToken] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isAvailable, setIsAvailable] = useState(true);

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string>("");
  const scriptLoadedRef = useRef(false);
  const isRenderedRef = useRef(false);

  // 使用 ref 存储回调，避免依赖变化导致重新渲染
  const callbacksRef = useRef({ onSuccess, onError, onExpire });
  callbacksRef.current = { onSuccess, onError, onExpire };

  // 获取站点密钥
  const siteKey = customSiteKey || getSiteKeyFromMeta();

  /**
   * 加载 Turnstile 脚本
   */
  const loadScript = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      // 如果已加载，直接返回
      if (window.turnstile) {
        scriptLoadedRef.current = true;
        resolve();
        return;
      }

      // 检查是否已有脚本标签
      if (
        document.querySelector(
          'script[src*="challenges.cloudflare.com/turnstile"]',
        )
      ) {
        // 等待脚本加载完成
        const checkInterval = setInterval(() => {
          if (window.turnstile) {
            clearInterval(checkInterval);
            scriptLoadedRef.current = true;
            resolve();
          }
        }, 100);

        // 超时处理
        setTimeout(() => {
          clearInterval(checkInterval);
          if (!window.turnstile) {
            reject(new Error("Turnstile script load timeout"));
          }
        }, SCRIPT_LOAD_TIMEOUT);
        return;
      }

      // 创建脚本标签
      const script = document.createElement("script");
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        scriptLoadedRef.current = true;
        resolve();
      };

      script.onerror = () => {
        reject(new Error("Failed to load Turnstile script"));
      };

      document.head.appendChild(script);

      // 超时处理
      setTimeout(() => {
        if (!window.turnstile) {
          reject(new Error("Turnstile script load timeout"));
        }
      }, SCRIPT_LOAD_TIMEOUT);
    });
  }, []);

  /**
   * 渲染 Widget
   */
  const renderWidget = useCallback(() => {
    if (!containerRef.current || !window.turnstile || !siteKey) {
      return;
    }

    // 如果已经渲染过，不重复渲染
    if (isRenderedRef.current && widgetIdRef.current) {
      return;
    }

    // 移除旧的 Widget
    if (widgetIdRef.current) {
      try {
        window.turnstile.remove(widgetIdRef.current);
      } catch {
        // 忽略移除错误
      }
      widgetIdRef.current = "";
    }

    // 确定主题
    const resolvedTheme = theme === "auto" ? getCurrentTheme() : theme;

    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (newToken: string) => {
          setToken(newToken);
          callbacksRef.current.onSuccess?.(newToken);
        },
        "error-callback": () => {
          setToken("");
          callbacksRef.current.onError?.();
        },
        "expired-callback": () => {
          setToken("");
          callbacksRef.current.onExpire?.();
        },
        theme: resolvedTheme,
        size,
      });
      isRenderedRef.current = true;
    } catch (error) {
      console.error("Failed to render Turnstile widget:", error);
      setIsAvailable(false);
    }
  }, [siteKey, theme, size]);

  /**
   * 重置验证
   */
  const reset = useCallback(() => {
    setToken("");
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch (error) {
        console.error("Failed to reset Turnstile:", error);
      }
    }
  }, []);

  /**
   * 移除 Widget
   */
  const remove = useCallback(() => {
    setToken("");
    isRenderedRef.current = false;
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = "";
      } catch (error) {
        console.error("Failed to remove Turnstile:", error);
      }
    }
  }, []);

  // 初始化：加载脚本并渲染 Widget（只执行一次）
  useEffect(() => {
    if (!siteKey) {
      console.warn("Turnstile site key not found");
      setIsAvailable(false);
      setIsLoading(false);
      return;
    }

    let mounted = true;

    const init = async () => {
      try {
        await loadScript();
        if (mounted && !isRenderedRef.current) {
          setIsLoading(false);
          renderWidget();
        }
      } catch (error) {
        console.warn("Turnstile initialization failed:", error);
        if (mounted) {
          setIsAvailable(false);
          setIsLoading(false);
        }
      }
    };

    init();

    return () => {
      mounted = false;
      remove();
    };
    // 只在 siteKey 变化时重新初始化
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  // 监听系统主题变化（仅在 auto 模式下）
  useEffect(() => {
    if (theme !== "auto" || !scriptLoadedRef.current || !isRenderedRef.current)
      return;

    // 使用 matchMedia 监听系统主题偏好，比 MutationObserver 更高效
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      isRenderedRef.current = false;
      renderWidget();
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme, renderWidget]);

  return {
    containerRef,
    token,
    isLoading,
    isAvailable,
    reset,
    remove,
  };
}
